// import React, { useState, useMemo, useEffect } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { useQueryClient, useQuery } from "@tanstack/react-query";
// import { usePartnerFormStore } from "../store/usePartnerFormStore";
// import { hexagonKeys } from "../hooks/useHexagonMap";
// import { getAllHexagons } from "../apis/hexagons";
// import {
//   Button,
//   RateSelector,
//   FullScreenModal,
//   NavigationButtons,
// } from "./ui";
// import { useServiceCategories } from "../hooks/usePartnerServices";
// import type { ServiceCategory } from "../apis/masterData";
// import { cn } from "../utils/cn";
// import { Badge } from "./ui/Badge";
// import { X, Search, Loader2 } from "lucide-react";
// import { useDebounce } from "../hooks/useDebounce";

// const rateOptions = [
//   {
//     value: "lower",
//     label: "Lower than market rate",
//     description: "Competitive pricing to attract more customers",
//   },
//   {
//     value: "same",
//     label: "Same as market rate",
//     description: "Standard pricing aligned with competitors",
//   },
//   {
//     value: "higher",
//     label: "Premium / Higher rate",
//     description: "Premium quality services with higher pricing",
//   },
// ];

// // API function to search categories
// const searchCategoriesAPI = async (searchTerm: string): Promise<ServiceCategory[]> => {
//   const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5002/api/loyalty";
//   const url = new URL(`${baseUrl}/api/loyalty/partner-services`);
//   if (searchTerm) {
//     url.searchParams.append("search", searchTerm);
//   }
//   url.searchParams.append("isActive", "true");
//   url.searchParams.append("limit", "50");
  
//   const response = await fetch(url.toString());
//   const data = await response.json();
//   return data.data || [];
// };

// const PartnerFormStep2: React.FC = () => {
//   const {
//     serviceDetails,
//     addService,
//     removeService,
//     previousStep,
//     nextStep,
//     errors,
//     setError,
//     clearError,
//   } = usePartnerFormStore();

//   const queryClient = useQueryClient();
//   useEffect(() => {
//     const subs = serviceDetails.services.map((s) => s.subcategory).filter(Boolean);
//     const key = subs.slice().sort().join(",");
//     queryClient.prefetchQuery({
//       queryKey: hexagonKeys.allWithSubcategory(key),
//       queryFn: () => getAllHexagons(subs),
//       staleTime: 30 * 1000,
//     });
//   }, [queryClient, serviceDetails.services]);

//   // Category search state
//   const [categorySearch, setCategorySearch] = useState("");
//   const debouncedCategorySearch = useDebounce(categorySearch, 300);
  
//   // Modal state
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [currentStep, setCurrentStep] = useState<1 | 2>(1);
//   const [editingIndex, setEditingIndex] = useState<number | null>(null);

//   // Form state for modal
//   const [selectedCategory, setSelectedCategory] = useState("");
//   const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
//   const [selectedServiceChargeRate, setSelectedServiceChargeRate] = useState("");

//   // Fetch categories with search from API
//   const {
//     data: categories = [],
//     isLoading: categoriesLoading,
//     isFetching: categoriesFetching,
//   } = useQuery({
//     queryKey: ["partner-services", "search", debouncedCategorySearch],
//     queryFn: () => searchCategoriesAPI(debouncedCategorySearch),
//     staleTime: 30000,
//     enabled: isModalOpen && currentStep === 1,
//   });

//   // Get current category data
//   const currentCategoryData = useMemo(() => 
//     categories.find((cat) => cat.category === selectedCategory),
//     [categories, selectedCategory]
//   );

//   const subcategoryOptions = useMemo(() => 
//     currentCategoryData
//       ? currentCategoryData.subcategories.map((sub) => ({
//           value: sub,
//           label: sub,
//         }))
//       : [],
//     [currentCategoryData]
//   );

//   // Check if category is already selected (only one allowed)
//   const hasSelectedCategory = serviceDetails.services.length > 0;
//   const selectedCategoryName = serviceDetails.services[0]?.category || "";

//   // Get existing subcategories to prevent duplicates
//   const existingSubcategories = useMemo(() => 
//     new Set(serviceDetails.services.map((s) => s.subcategory)),
//     [serviceDetails.services]
//   );

//   // Filter out already selected subcategories (except when editing)
//   const availableSubcategories = useMemo(() => {
//     if (editingIndex !== null) {
//       const editingService = serviceDetails.services[editingIndex];
//       return subcategoryOptions.filter(
//         (opt) => !existingSubcategories.has(opt.value) || opt.value === editingService?.subcategory
//       );
//     }
//     return subcategoryOptions.filter((opt) => !existingSubcategories.has(opt.value));
//   }, [subcategoryOptions, existingSubcategories, editingIndex, serviceDetails.services]);

//   // Handle category selection
//   const handleCategorySelect = (category: string) => {
//     setSelectedCategory(category);
//     setCategorySearch("");
//     setSelectedSubcategories([]);
//     setCurrentStep(2);
//     clearError("category");
//   };

//   // Handle subcategory toggle (multi-select)
//   const handleSubcategoryToggle = (subcategory: string) => {
//     if (selectedSubcategories.includes(subcategory)) {
//       setSelectedSubcategories(selectedSubcategories.filter(s => s !== subcategory));
//     } else {
//       setSelectedSubcategories([...selectedSubcategories, subcategory]);
//     }
//     clearError("subcategory");
//   };

//   // Reset modal form
//   const resetModalForm = () => {
//     setSelectedCategory("");
//     setSelectedSubcategories([]);
//     setSelectedServiceChargeRate("");
//     setCurrentStep(1);
//     setEditingIndex(null);
//     setCategorySearch("");
//     clearError("category");
//     clearError("subcategory");
//     clearError("serviceChargeRate");
//   };

//   // Handle adding/updating service
//   const handleAddService = () => {
//     if (!selectedCategory) {
//       setError("category", "Please select a category");
//       return;
//     }

//     if (selectedSubcategories.length === 0) {
//       setError("subcategory", "Please select at least one subcategory");
//       return;
//     }

//     if (!selectedServiceChargeRate) {
//       setError("serviceChargeRate", "Please select service charge rate");
//       return;
//     }

//     // Check if trying to add a different category when already have one
//     if (hasSelectedCategory && selectedCategory !== selectedCategoryName && editingIndex === null) {
//       setError("category", "Only one category can be selected. Please remove existing services first.");
//       return;
//     }

//     if (editingIndex !== null) {
//       // Remove the old service when editing
//       removeService(editingIndex);
//     }

//     // Add a service for each selected subcategory
//     selectedSubcategories.forEach((subcategory) => {
//       addService({
//         category: selectedCategory,
//         subcategory: subcategory,
//         serviceChargeCategory: selectedServiceChargeRate as "lower" | "same" | "higher",
//       });
//     });

//     // Reset and close
//     resetModalForm();
//     setIsModalOpen(false);
//   };

//   // Handle edit service
//   const handleEditService = (index: number) => {
//     const service = serviceDetails.services[index];
//     setEditingIndex(index);
//     setSelectedCategory(service.category);
//     setSelectedSubcategories([service.subcategory]);
//     setSelectedServiceChargeRate(service.serviceChargeCategory);
//     setCurrentStep(2);
//     setIsModalOpen(true);
//     clearError("category");
//     clearError("subcategory");
//     clearError("serviceChargeRate");
//   };

//   const validateStep = (): boolean => {
//     if (serviceDetails.services.length === 0) {
//       setError("services", "At least one service is required");
//       return false;
//     }

//     const servicesWithoutRate = serviceDetails.services.filter(
//       (s) => !s.serviceChargeCategory
//     );
//     if (servicesWithoutRate.length > 0) {
//       setError("services", "All services must have a service charge rate category");
//       return false;
//     }

//     // Check unique categories (max 1)
//     const uniqueCategories = new Set(serviceDetails.services.map((s) => s.category));
//     if (uniqueCategories.size > 1) {
//       setError("services", "Only one category allowed");
//       return false;
//     }

//     return true;
//   };

//   const handleNext = () => {
//     if (validateStep()) {
//       nextStep();
//     }
//   };

//   // Group services by category for display
//   const groupedServices = useMemo(() => {
//     const grouped: Record<string, Array<{ subcategory: string; index: number; rate: string }>> = {};
//     serviceDetails.services.forEach((service, index) => {
//       if (!grouped[service.category]) {
//         grouped[service.category] = [];
//       }
//       grouped[service.category].push({
//         subcategory: service.subcategory,
//         index,
//         rate: service.serviceChargeCategory || '',
//       });
//     });
//     return grouped;
//   }, [serviceDetails.services]);

//   // Calculate remaining slots (no limit on subcategories)
//   const remainingCategories = hasSelectedCategory ? 0 : 1;

//   // Show loading state for initial data (only when modal first opens and no categories loaded)
//   const isLoading = categoriesLoading && categories.length === 0;

//   return (
//     <div className="space-y-5 md:space-y-6">
//       {/* Header */}
//       <div>
//         <h2 className="text-lg md:text-2xl font-semibold text-secondary-900 mb-1">
//           Select Your Services
//         </h2>
//         <p className="text-sm md:text-base text-secondary-600">
//           Choose 1 category and any number of subcategories
//         </p>
//       </div>

//       {/* Limits indicator */}
//       <div className="flex flex-wrap gap-3">
//         <div className={cn(
//           "px-3 py-1.5 rounded-full text-xs font-medium",
//           remainingCategories > 0 ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
//         )}>
//           {remainingCategories > 0 ? `${remainingCategories} category remaining` : "Category selected"}
//         </div>
//         <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
//           Unlimited subcategories
//         </div>
//       </div>

//       {/* Empty state */}
//       {serviceDetails.services.length === 0 && (
//         <div className="rounded-xl border border-dashed border-secondary-200 bg-secondary-50/50 p-6 md:p-8 space-y-4 min-h-[200px] flex flex-col justify-center">
//           <p className="text-sm text-secondary-600 text-center">
//             Add services you want to offer. You can choose 1 category and any number of subcategories.
//           </p>
//           <div className="flex flex-wrap justify-center gap-2 text-xs text-secondary-500">
//             <span className="px-2 py-1 bg-white rounded-lg border border-secondary-100">1. Select category</span>
//             <span className="px-2 py-1 bg-white rounded-lg border border-secondary-100">2. Pick subcategories</span>
//             <span className="px-2 py-1 bg-white rounded-lg border border-secondary-100">3. Set pricing</span>
//           </div>
//         </div>
//       )}

//       {/* Selected Services Display with Edit option */}
//       {serviceDetails.services.length > 0 && (
//         <div className="space-y-3">
//           <h3 className="text-sm font-medium text-secondary-700">
//             Your Selected Services:
//           </h3>
//           <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
//             <AnimatePresence>
//               {Object.entries(groupedServices).map(([category, subcategories]) => (
//                 <motion.div
//                   key={category}
//                   initial={{ opacity: 0, y: -10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: -10 }}
//                   className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-4 border border-primary-100"
//                 >
//                   <div className="flex items-start justify-between gap-3">
//                     <div className="flex-1 min-w-0">
//                       <div className="flex items-center gap-2 mb-2">
//                         <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
//                           <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
//                           </svg>
//                         </div>
//                         <h4 className="font-semibold text-secondary-900">{category}</h4>
//                       </div>
                      
//                       <div className="flex flex-wrap gap-2">
//                         {subcategories.map(({ subcategory, index, rate }) => (
//                           <motion.span
//                             key={`${category}-${subcategory}-${index}`}
//                             layout
//                             className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-secondary-700 rounded-full text-sm border border-secondary-200 shadow-sm"
//                           >
//                             <span>{subcategory}</span>
//                             <button
//                               type="button"
//                               onClick={() => handleEditService(index)}
//                               className="ml-1 p-0.5 hover:bg-blue-100 rounded-full transition-colors text-secondary-400 hover:text-blue-600"
//                               title={`Edit ${subcategory}`}
//                             >
//                               <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
//                               </svg>
//                             </button>
//                             <button
//                               type="button"
//                               onClick={() => removeService(index)}
//                               className="p-0.5 hover:bg-red-100 rounded-full transition-colors text-secondary-400 hover:text-red-500"
//                               title={`Remove ${subcategory}`}
//                             >
//                               <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                               </svg>
//                             </button>
//                           </motion.span>
//                         ))}
//                       </div>

//                       {/* Rate badge */}
//                       {subcategories[0]?.rate && (
//                         <div className="mt-2">
//                           <span className={cn(
//                             "text-xs px-2 py-0.5 rounded-full",
//                             subcategories[0].rate === 'lower' && "bg-emerald-100 text-emerald-700",
//                             subcategories[0].rate === 'same' && "bg-blue-100 text-blue-700",
//                             subcategories[0].rate === 'higher' && "bg-violet-100 text-violet-700"
//                           )}>
//                             {subcategories[0].rate === 'lower' ? 'Lower than market' : 
//                              subcategories[0].rate === 'same' ? 'Market rate' : 'Premium rate'}
//                           </span>
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 </motion.div>
//               ))}
//             </AnimatePresence>
//           </div>
//         </div>
//       )}

//       {/* Add Service Button */}
//       {(!hasSelectedCategory || (hasSelectedCategory && serviceDetails.services.length > 0)) && (
//         <Button
//           onClick={() => setIsModalOpen(true)}
//           variant="primary"
//           fullWidth
//           className="min-h-[52px] text-base font-semibold shadow-lg hover:shadow-xl"
//         >
//           <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
//           </svg>
//           {hasSelectedCategory ? "Add More Subcategories" : "Add Service"}
//         </Button>
//       )}

//       {/* Add/Edit Service Modal */}
//       <FullScreenModal
//         isOpen={isModalOpen}
//         onClose={() => {
//           setIsModalOpen(false);
//           resetModalForm();
//         }}
//         title={
//           currentStep === 1 ? "Select Category" :
//           editingIndex !== null ? "Edit Service" : "Select Subcategories & Pricing"
//         }
//       >
//         <div className="flex flex-col h-full">
//           <div className="flex-1 overflow-y-auto p-4 md:p-6">
//             {/* Step 1: Category Selection with Search */}
//             {currentStep === 1 && (
//               <div className="space-y-4">
//                 <p className="text-sm text-secondary-600">
//                   Choose a service category to get started
//                 </p>
                
//                 {/* Search Input with Loading Indicator */}
//                 <div className="relative">
//                   <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
//                   <input
//                     type="text"
//                     placeholder="Search categories..."
//                     value={categorySearch}
//                     onChange={(e) => setCategorySearch(e.target.value)}
//                     className="w-full pl-9 pr-10 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
//                   />
//                   {(categoriesLoading || categoriesFetching) && (
//                     <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400 animate-spin" />
//                   )}
//                 </div>

//                 {/* Categories Grid */}
//                 <div className="space-y-2">
//                   {isLoading ? (
//                     <div className="flex items-center justify-center py-8">
//                       <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
//                       <span className="ml-2 text-secondary-500">Loading categories...</span>
//                     </div>
//                   ) : categories.length === 0 ? (
//                     <div className="p-4 text-center text-secondary-500 border border-secondary-200 rounded-lg">
//                       {categorySearch ? "No categories found matching your search" : "No categories available"}
//                     </div>
//                   ) : (
//                     categories.map((cat) => {
//                       const isDisabled = hasSelectedCategory && selectedCategoryName !== cat.category;
//                       return (
//                         <button
//                           key={cat.category}
//                           type="button"
//                           onClick={() => {
//                             if (!isDisabled) {
//                               handleCategorySelect(cat.category);
//                             }
//                           }}
//                           className={cn(
//                             "w-full text-left px-4 py-3 rounded-lg border transition-all",
//                             isDisabled 
//                               ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-200" 
//                               : "hover:bg-primary-50 hover:border-primary-200 cursor-pointer border-gray-200",
//                             selectedCategory === cat.category && "bg-primary-50 border-primary-300 ring-2 ring-primary-200"
//                           )}
//                           disabled={isDisabled}
//                         >
//                           <div className="flex justify-between items-center">
//                             <span className="font-medium text-secondary-900">{cat.category}</span>
//                             <span className="text-xs text-secondary-500 bg-gray-100 px-2 py-1 rounded-full">
//                               {cat.subcategories?.length || 0} subcategories
//                             </span>
//                           </div>
//                         </button>
//                       );
//                     })
//                   )}
//                 </div>

//                 {errors.category && (
//                   <p className="text-sm text-red-600">{errors.category as string}</p>
//                 )}
//               </div>
//             )}

//             {/* Step 2: Subcategory Selection & Pricing */}
//             {currentStep === 2 && (
//               <div className="space-y-4">
//                 <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white pt-0 pb-2">
//                   {editingIndex === null && (
//                     <button
//                       onClick={() => setCurrentStep(1)}
//                       className="p-1.5 hover:bg-secondary-100 rounded-lg transition-colors"
//                     >
//                       <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
//                       </svg>
//                     </button>
//                   )}
//                   <div>
//                     <p className="text-xs text-secondary-500">Category</p>
//                     <p className="font-semibold text-secondary-900">{selectedCategory || selectedCategoryName}</p>
//                   </div>
//                 </div>

//                 {/* Subcategory Selection */}
//                 <div className="space-y-3">
//                   <label className="text-sm font-medium text-secondary-700">
//                     Select Subcategories (multiple allowed)
//                   </label>
//                   <div className="border border-secondary-200 rounded-lg divide-y divide-secondary-100">
//                     {availableSubcategories.length === 0 ? (
//                       <div className="p-4 text-center text-secondary-500">
//                         No subcategories available
//                       </div>
//                     ) : (
//                       availableSubcategories.map((opt) => (
//                         <label
//                           key={opt.value}
//                           className="flex items-center gap-3 px-4 py-3 hover:bg-secondary-50 cursor-pointer"
//                         >
//                           <input
//                             type="checkbox"
//                             checked={selectedSubcategories.includes(opt.value)}
//                             onChange={() => handleSubcategoryToggle(opt.value)}
//                             className="w-4 h-4 text-primary-600 rounded border-secondary-300 focus:ring-primary-500"
//                           />
//                           <span className="text-secondary-700">{opt.label}</span>
//                         </label>
//                       ))
//                     )}
//                   </div>
//                   <p className="text-xs text-secondary-500">
//                     {selectedSubcategories.length} subcategor{selectedSubcategories.length !== 1 ? 'ies' : 'y'} selected
//                   </p>
//                 </div>

//                 {/* Selected Subcategories Badges */}
//                 {selectedSubcategories.length > 0 && (
//                   <div className="mt-2">
//                     <label className="text-sm font-medium text-secondary-700 mb-2 block">
//                       Selected Subcategories:
//                     </label>
//                     <div className="flex flex-wrap gap-2">
//                       {selectedSubcategories.map((sub) => (
//                         <Badge key={sub} variant="secondary" className="gap-1 px-3 py-1.5">
//                           {sub}
//                           <X
//                             className="h-3 w-3 cursor-pointer ml-1 hover:text-destructive"
//                             onClick={() => handleSubcategoryToggle(sub)}
//                           />
//                         </Badge>
//                       ))}
//                     </div>
//                   </div>
//                 )}

//                 {/* Rate Selection */}
//                 {selectedSubcategories.length > 0 && (
//                   <>
//                     <div className="pt-4">
//                       <RateSelector
//                         label="Your Service Charge Rate"
//                         options={rateOptions}
//                         value={selectedServiceChargeRate}
//                         onChange={(value) => {
//                           setSelectedServiceChargeRate(value);
//                           clearError("serviceChargeRate");
//                         }}
//                         error={errors.serviceChargeRate as string}
//                       />
//                     </div>

//                     <Button
//                       onClick={handleAddService}
//                       variant="primary"
//                       fullWidth
//                       disabled={!selectedServiceChargeRate}
//                       className="mt-4 min-h-[52px]"
//                     >
//                       <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                       </svg>
//                       {editingIndex !== null ? "Update Service" : `Add ${selectedSubcategories.length} Subcategor${selectedSubcategories.length !== 1 ? 'ies' : 'y'}`}
//                     </Button>
//                   </>
//                 )}
//               </div>
//             )}
//           </div>
//         </div>
//       </FullScreenModal>

//       {/* Error message for services */}
//       {errors.services && (
//         <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
//           <p className="text-sm text-red-600">{errors.services as string}</p>
//         </div>
//       )}

//       {/* Navigation Buttons */}
//       <NavigationButtons
//         onPrevious={previousStep}
//         onNext={handleNext}
//         nextLabel="Next: Serviceable Areas"
//       />
//     </div>
//   );
// };

// export default PartnerFormStep2;

import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { usePartnerFormStore } from "../store/usePartnerFormStore";
import { hexagonKeys } from "../hooks/useHexagonMap";
import { getAllHexagons } from "../apis/hexagons";
import {
  Button,
  RateSelector,
  FullScreenModal,
  NavigationButtons,
} from "./ui";
import type { ServiceCategory } from "../apis/masterData";
import { cn } from "../utils/cn";
import { Badge } from "./ui/Badge";
import { X, Search, Loader2, Edit2, Plus } from "lucide-react";

const MAX_SUBCATEGORIES = 3;

const rateOptions = [
  {
    value: "lower",
    label: "Lower than market rate",
    description: "Competitive pricing to attract more customers",
  },
  {
    value: "same",
    label: "Same as market rate",
    description: "Standard pricing aligned with competitors",
  },
  {
    value: "higher",
    label: "Premium / Higher rate",
    description: "Premium quality services with higher pricing",
  },
];

// API function to search categories
const searchCategoriesAPI = async (searchTerm: string): Promise<ServiceCategory[]> => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:5002/api/loyalty";
  const url = new URL(`${baseUrl}/api/loyalty/partner-services`);
  if (searchTerm) {
    url.searchParams.append("search", searchTerm);
  }
  url.searchParams.append("isActive", "true");
  url.searchParams.append("limit", "50");
  
  const response = await fetch(url.toString());
  const data = await response.json();
  return data.data || [];
};

const PartnerFormStep2: React.FC = () => {
  const {
    serviceDetails,
    setService,
    previousStep,
    nextStep,
    errors,
    setError,
    clearError,
  } = usePartnerFormStore();

  const queryClient = useQueryClient();
  
  // Get current service - safely access with default values
  const currentService = serviceDetails.services || { category: '', subcategories: [], serviceChargeCategory: '' };
  const hasService = !!currentService.category;
  
  // Safely get subcategories array (default to empty array if undefined)
  const currentSubcategories = currentService.subcategories || [];
  
  // Get all subcategories for hexagon prefetch
  useEffect(() => {
    if (currentSubcategories.length > 0) {
      const key = currentSubcategories.slice().sort().join(",");
      queryClient.prefetchQuery({
        queryKey: hexagonKeys.allWithSubcategory(key),
        queryFn: () => getAllHexagons(currentSubcategories),
        staleTime: 30 * 1000,
      });
    }
  }, [queryClient, currentSubcategories]);

  // Category search state
  const [categorySearch, setCategorySearch] = useState("");
  const debouncedCategorySearch = useDebounce(categorySearch, 300);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  // Form state for modal
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [selectedServiceChargeRate, setSelectedServiceChargeRate] = useState("");

  // Fetch categories with search from API
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isFetching: categoriesFetching,
  } = useQuery({
    queryKey: ["partner-services", "search", debouncedCategorySearch],
    queryFn: () => searchCategoriesAPI(debouncedCategorySearch),
    staleTime: 30000,
    enabled: isModalOpen && currentStep === 1,
  });

  // Get current category data
  const currentCategoryData = useMemo(() => 
    categories.find((cat) => cat.category === selectedCategory),
    [categories, selectedCategory]
  );

  const subcategoryOptions = useMemo(() => 
    currentCategoryData
      ? currentCategoryData.subcategories.map((sub) => ({
          value: sub,
          label: sub,
        }))
      : [],
    [currentCategoryData]
  );

  // Handle category selection
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setSelectedSubcategories([]);
    setCurrentStep(2);
    clearError("category");
  };

  // Handle subcategory toggle with limit of 3
  const handleSubcategoryToggle = (subcategory: string) => {
    if (selectedSubcategories.includes(subcategory)) {
      setSelectedSubcategories(selectedSubcategories.filter(s => s !== subcategory));
      clearError("subcategory");
    } else {
      if (selectedSubcategories.length >= MAX_SUBCATEGORIES) {
        setError("subcategory", `You can select a maximum of ${MAX_SUBCATEGORIES} subcategories`);
        return;
      }
      setSelectedSubcategories([...selectedSubcategories, subcategory]);
      clearError("subcategory");
    }
  };

  // Reset modal form
  const resetModalForm = () => {
    setSelectedCategory("");
    setSelectedSubcategories([]);
    setSelectedServiceChargeRate("");
    setCurrentStep(1);
    setCategorySearch("");
    clearError("category");
    clearError("subcategory");
    clearError("serviceChargeRate");
  };

  // Handle adding/updating service
  const handleAddService = () => {
    if (!selectedCategory) {
      setError("category", "Please select a category");
      return;
    }

    if (selectedSubcategories.length === 0) {
      setError("subcategory", "Please select at least one subcategory");
      return;
    }

    if (selectedSubcategories.length > MAX_SUBCATEGORIES) {
      setError("subcategory", `You can select a maximum of ${MAX_SUBCATEGORIES} subcategories`);
      return;
    }

    if (!selectedServiceChargeRate) {
      setError("serviceChargeRate", "Please select service charge rate");
      return;
    }

    // Set the service
    setService({
      category: selectedCategory,
      subcategories: selectedSubcategories,
      serviceChargeCategory: selectedServiceChargeRate as "lower" | "same" | "higher",
    });

    // Reset and close
    resetModalForm();
    setIsModalOpen(false);
  };

  const validateStep = (): boolean => {
    if (!hasService) {
      setError("services", "Please select a service");
      return false;
    }

    if (!currentService.serviceChargeCategory) {
      setError("services", "Service must have a service charge rate category");
      return false;
    }

    if (currentSubcategories.length === 0) {
      setError("services", "Please select at least one subcategory");
      return false;
    }

    if (currentSubcategories.length > MAX_SUBCATEGORIES) {
      setError("services", `You can select a maximum of ${MAX_SUBCATEGORIES} subcategories`);
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      nextStep();
    }
  };

  return (
    <div className="space-y-5 md:space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg md:text-2xl font-semibold text-secondary-900 mb-1">
          Select Your Services
        </h2>
        <p className="text-sm md:text-base text-secondary-600">
          Choose 1 category and up to {MAX_SUBCATEGORIES} subcategories
        </p>
      </div>

      {/* Limits indicator */}
      <div className="flex flex-wrap gap-3">
        <div className={cn(
          "px-3 py-1.5 rounded-full text-xs font-medium",
          hasService ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
        )}>
          {hasService ? "Category selected" : "1 category remaining"}
        </div>
        <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
          Max {MAX_SUBCATEGORIES} subcategories
        </div>
      </div>

      {/* Empty state */}
      {!hasService && (
        <div className="rounded-xl border border-dashed border-secondary-200 bg-secondary-50/50 p-6 md:p-8 space-y-4 min-h-[200px] flex flex-col justify-center">
          <p className="text-sm text-secondary-600 text-center">
            Add services you want to offer. You can choose 1 category and up to {MAX_SUBCATEGORIES} subcategories.
          </p>
          <div className="flex flex-wrap justify-center gap-2 text-xs text-secondary-500">
            <span className="px-2 py-1 bg-white rounded-lg border border-secondary-100">1. Select category</span>
            <span className="px-2 py-1 bg-white rounded-lg border border-secondary-100">2. Pick subcategories</span>
            <span className="px-2 py-1 bg-white rounded-lg border border-secondary-100">3. Set pricing</span>
          </div>
        </div>
      )}

      {/* Selected Service Display */}
      {hasService && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-secondary-700">
            Your Selected Service:
          </h3>
          <div className="space-y-3">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-4 border border-primary-100"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-secondary-900">{currentService.category}</h4>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {currentSubcategories.map((subcategory) => (
                      <span
                        key={subcategory}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-secondary-700 rounded-full text-sm border border-secondary-200 shadow-sm"
                      >
                        <span>{subcategory}</span>
                      </span>
                    ))}
                  </div>

                  {/* Rate badge */}
                  {currentService.serviceChargeCategory && (
                    <div className="mt-3">
                      <span className={cn(
                        "text-xs px-2 py-0.5 rounded-full",
                        currentService.serviceChargeCategory === 'lower' && "bg-emerald-100 text-emerald-700",
                        currentService.serviceChargeCategory === 'same' && "bg-blue-100 text-blue-700",
                        currentService.serviceChargeCategory === 'higher' && "bg-violet-100 text-violet-700"
                      )}>
                        {currentService.serviceChargeCategory === 'lower' ? 'Lower than market' : 
                         currentService.serviceChargeCategory === 'same' ? 'Market rate' : 'Premium rate'}
                      </span>
                    </div>
                  )}
                </div>
                
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors text-secondary-400 hover:text-blue-600"
                  title="Edit service"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Add/Edit Service Button */}
      {!hasService && (
        <Button
          onClick={() => setIsModalOpen(true)}
          variant="primary"
          fullWidth
          className="min-h-[52px] text-base font-semibold shadow-lg hover:shadow-xl"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Service
        </Button>
      )}

      {/* Add/Edit Service Modal */}
      <FullScreenModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetModalForm();
        }}
        title={
          currentStep === 1 ? "Select Category" : "Select Subcategories & Pricing"
        }
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Step 1: Category Selection with Search */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-secondary-600">
                  Choose a service category to get started
                </p>
                
                {/* Search Input with Loading Indicator */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Search categories..."
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    className="w-full pl-9 pr-10 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {(categoriesLoading || categoriesFetching) && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400 animate-spin" />
                  )}
                </div>

                {/* Categories Grid */}
                <div className="space-y-2">
                  {categoriesLoading && categories.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
                      <span className="ml-2 text-secondary-500">Loading categories...</span>
                    </div>
                  ) : categories.length === 0 ? (
                    <div className="p-4 text-center text-secondary-500 border border-secondary-200 rounded-lg">
                      {categorySearch ? "No categories found matching your search" : "No categories available"}
                    </div>
                  ) : (
                    categories.map((cat) => {
                      return (
                        <button
                          key={cat.category}
                          type="button"
                          onClick={() => handleCategorySelect(cat.category)}
                          className={cn(
                            "w-full text-left px-4 py-3 rounded-lg border transition-all",
                            "hover:bg-primary-50 hover:border-primary-200 cursor-pointer border-gray-200",
                            selectedCategory === cat.category && "bg-primary-50 border-primary-300 ring-2 ring-primary-200"
                          )}
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-secondary-900">{cat.category}</span>
                            <span className="text-xs text-secondary-500 bg-gray-100 px-2 py-1 rounded-full">
                              {cat.subcategories?.length || 0} subcategories
                            </span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {errors.category && (
                  <p className="text-sm text-red-600">{errors.category as string}</p>
                )}
              </div>
            )}

            {/* Step 2: Subcategory Selection & Pricing */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white pt-0 pb-2">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="p-1.5 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div>
                    <p className="text-xs text-secondary-500">Category</p>
                    <p className="font-semibold text-secondary-900">{selectedCategory}</p>
                  </div>
                </div>

                {/* Subcategory Selection with Limit */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-secondary-700">
                      Select Subcategories (max {MAX_SUBCATEGORIES})
                    </label>
                    <span className={cn(
                      "text-xs font-medium px-2 py-1 rounded-full",
                      selectedSubcategories.length === MAX_SUBCATEGORIES 
                        ? "bg-red-100 text-red-700" 
                        : "bg-gray-100 text-gray-600"
                    )}>
                      {selectedSubcategories.length}/{MAX_SUBCATEGORIES} selected
                    </span>
                  </div>
                  <div className="border border-secondary-200 rounded-lg divide-y divide-secondary-100 max-h-96 overflow-y-auto">
                    {subcategoryOptions.length === 0 ? (
                      <div className="p-4 text-center text-secondary-500">
                        No subcategories available
                      </div>
                    ) : (
                      subcategoryOptions.map((opt) => {
                        const isSelected = selectedSubcategories.includes(opt.value);
                        const isDisabled = !isSelected && selectedSubcategories.length >= MAX_SUBCATEGORIES;
                        
                        return (
                          <label
                            key={opt.value}
                            className={cn(
                              "flex items-center gap-3 px-4 py-3 transition-colors",
                              isDisabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "hover:bg-secondary-50 cursor-pointer"
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSubcategoryToggle(opt.value)}
                              disabled={isDisabled}
                              className="w-4 h-4 text-primary-600 rounded border-secondary-300 focus:ring-primary-500 disabled:opacity-50"
                            />
                            <span className="text-secondary-700">{opt.label}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  {errors.subcategory && (
                    <p className="text-xs text-red-600">{errors.subcategory as string}</p>
                  )}
                  <p className="text-xs text-secondary-500">
                    You can select up to {MAX_SUBCATEGORIES} subcategories
                  </p>
                </div>

                {/* Selected Subcategories Badges */}
                {selectedSubcategories.length > 0 && (
                  <div className="mt-2">
                    <label className="text-sm font-medium text-secondary-700 mb-2 block">
                      Selected Subcategories:
                    </label>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-secondary-50 rounded-lg">
                      {selectedSubcategories.map((sub) => (
                        <Badge key={sub} variant="secondary" className="gap-1 px-3 py-1.5">
                          {sub}
                          <X
                            className="h-3 w-3 cursor-pointer ml-1 hover:text-destructive"
                            onClick={() => handleSubcategoryToggle(sub)}
                          />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rate Selection */}
                {selectedSubcategories.length > 0 && (
                  <>
                    <div className="pt-4">
                      <RateSelector
                        label="Your Service Charge Rate"
                        options={rateOptions}
                        value={selectedServiceChargeRate}
                        onChange={(value) => {
                          setSelectedServiceChargeRate(value);
                          clearError("serviceChargeRate");
                        }}
                        error={errors.serviceChargeRate as string}
                      />
                    </div>

                    <Button
                      onClick={handleAddService}
                      variant="primary"
                      fullWidth
                      disabled={!selectedServiceChargeRate}
                      className="mt-4 min-h-[52px]"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {hasService ? "Update Service" : `Add ${selectedSubcategories.length} Subcategor${selectedSubcategories.length !== 1 ? 'ies' : 'y'}`}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </FullScreenModal>

      {/* Error message for services */}
      {errors.services && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{errors.services as string}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <NavigationButtons
        onPrevious={previousStep}
        onNext={handleNext}
        nextLabel="Next: Serviceable Areas"
      />
    </div>
  );
};

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default PartnerFormStep2;